import React from 'react'
import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core'
import { CommissionDraftContext, CommissionInvoiceLine } from './state/commissionsSlice'
import { formatPesos } from '../../utils/format'

interface Props {
  context: CommissionDraftContext
  busy: boolean
  onKeep: () => void
  onReplace: () => void
}

const lineAmount = (line: CommissionInvoiceLine) =>
  (Number(line.amount) || 0) + (Number(line.promotionAmount) || 0) - (Number(line.prepaymentAmount) || 0)

const LinesTable: React.FC<{
  title: string
  lines: CommissionInvoiceLine[]
}> = ({ title, lines }) => {
  if (!lines.length) return null
  return (
    <Box marginTop="1rem">
      <Typography variant="subtitle1">{title}</Typography>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            <TableCell>Referido</TableCell>
            <TableCell>Concepto</TableCell>
            <TableCell align="right">Valor</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map(line => (
            <TableRow key={line.lineKey || `${line.personId}-${line.isDeduction ? 'deduction' : 'commission'}`}>
              <TableCell>{line.name}</TableCell>
              <TableCell>{line.isDeduction || line.kind === 'deduction' ? 'Deducción' : 'Comisión'}</TableCell>
              <TableCell align="right">$ {formatPesos(Math.round(lineAmount(line)))}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

const DraftContextPanel: React.FC<Props> = ({ context, busy, onKeep, onReplace }) => {
  const { activeDraft, comparison, currentClaim, acknowledgement } = context
  if (!activeDraft) return null

  const invalid = comparison.status === 'invalidating_changes'
  const additions = comparison.status === 'additions_only'
  const requestDecision = additions && !acknowledgement?.validForToday
  const changedLines = [...comparison.modified.map(item => item.current), ...comparison.removed]

  return (
    <Paper
      style={{
        width: '100%',
        padding: '1rem',
        marginBottom: '1rem',
        borderLeft: '4px solid #ff6900'
      }}
    >
      <Typography variant="h6">Cuenta de cobro pendiente {activeDraft.serial}</Typography>
      <Typography>
        Valor reservado: $ {formatPesos(activeDraft.amount)} · Valor disponible hoy: ${' '}
        {formatPesos(currentClaim.amount)} · Diferencia: $ {formatPesos(currentClaim.amount - activeDraft.amount)}
      </Typography>
      {invalid && (
        <Typography color="error" id="invalid-draft-reason">
          Algunos conceptos reservados cambiaron o dejaron de estar vigentes. Debes generar una cuenta actualizada antes
          de descargar o subir.
        </Typography>
      )}
      {requestDecision && (
        <Typography>
          Hay nuevas comisiones disponibles. Puedes consolidarlas en una cuenta nueva o continuar hoy con el borrador
          anterior.
        </Typography>
      )}

      <LinesTable title="Incluidas en el borrador actual" lines={comparison.unchanged} />
      <LinesTable title="Nuevas comisiones disponibles" lines={comparison.added} />
      <LinesTable title="Conceptos modificados o no vigentes" lines={changedLines} />

      {(requestDecision || invalid) && (
        <Box display="flex" justifyContent="flex-end" marginTop="1rem">
          <Button
            variant="outlined"
            color="primary"
            onClick={onKeep}
            disabled={busy || invalid}
            aria-describedby={invalid ? 'invalid-draft-reason' : undefined}
          >
            Conservar borrador anterior
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onReplace}
            disabled={busy}
            style={{ marginLeft: '0.75rem' }}
          >
            Actualizar cuenta de cobro
          </Button>
        </Box>
      )}
    </Paper>
  )
}

export default DraftContextPanel
